import type {
  FeedProductInterface,
  FormattedFeedProductType,
  SegmentOfferPrice,
  SegmentPriceOfferKey,
  SegmentPricesResponse,
} from '@/types'
import { getRowOfferPrice } from '@/utils/products/offerPrice'

export const MAX_SEGMENT_PRICE_OFFERS = 1000

export const getRowSegmentOffer = (
  row: FeedProductInterface
): SegmentPriceOfferKey | null => {
  const productCode = row.nomenclatureDto.code
  const stock = row.suppliersStock[0]?.stock[0]
  if (stock?.route?.length) {
    return {
      productCode,
      supplierId: stock.route[0],
      storeCode: stock.storeShortModel.code,
      route: stock.route,
    }
  }
  const route = getRowOfferPrice(row)?.route
  return route?.length ? { productCode, route } : null
}

const getOfferKey = ({
  productCode,
  supplierId,
  storeCode,
  route,
}: SegmentPriceOfferKey) =>
  [productCode, supplierId ?? '', storeCode ?? '', route.join('>')].join('|')

const getRows = (products: FormattedFeedProductType[]) =>
  products.flatMap((product) => [product, ...(product.children ?? [])])

export const collectSegmentPriceOffers = (
  products: FormattedFeedProductType[]
) => {
  const offers = new Map<string, SegmentPriceOfferKey>()
  getRows(products).forEach((row) => {
    const offer = getRowSegmentOffer(row)
    if (offer) offers.set(getOfferKey(offer), offer)
  })
  return Array.from(offers.values()).slice(0, MAX_SEGMENT_PRICE_OFFERS)
}

const withSegmentPrices = <T extends FeedProductInterface>(
  row: T,
  pricesByKey: Map<string, SegmentOfferPrice[]>
): T => {
  const offer = getRowSegmentOffer(row)
  const segmentPrices = offer ? pricesByKey.get(getOfferKey(offer)) : undefined
  const rowPrice = getRowOfferPrice(row)
  const [supplier, ...otherSuppliers] = row.suppliersStock
  if (!segmentPrices || !rowPrice || !supplier) return row

  return {
    ...row,
    suppliersStock: [
      {
        ...supplier,
        prices: supplier.prices.map((price) =>
          price === rowPrice ? { ...price, segmentPrices } : price
        ),
      },
      ...otherSuppliers,
    ],
  }
}

export const applySegmentPrices = (
  products: FormattedFeedProductType[],
  response: Pick<SegmentPricesResponse, 'offers'>
): FormattedFeedProductType[] => {
  const pricesByKey = new Map(
    response.offers.flatMap((offer) =>
      offer.segmentPrices ? [[getOfferKey(offer), offer.segmentPrices]] : []
    )
  )
  return products.map((product) => ({
    ...withSegmentPrices(product, pricesByKey),
    children: product.children?.map((child) =>
      withSegmentPrices(child, pricesByKey)
    ),
  }))
}

export const getSegmentPriceDiffPercent = (
  price: number,
  contextPrice: number | null | undefined
) => {
  if (!contextPrice) return null
  return Math.round(((price - contextPrice) / contextPrice) * 1000) / 10
}
